/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { EDIT_ORGANISATION_TRANSLATION_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import { IOrganizationTranslationData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import NumberField from "../inputs/NumberField";
import ToggleField from "../inputs/ToggleField";
import {
  CREATE_ORG_TRANSLATION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";

const set = new Set();

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = EDIT_ORGANISATION_TRANSLATION_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewTranslationForm = ({ type, org }: { type: string; org: string }) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] =
    useState<IOrganizationTranslationData>({
      FROM: undefined,
      EFI: false,
      EFO: false,
      TO: undefined,
    });

  const createTranslation = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/translations/${type}`,
        method: "POST",
        body: JSON.stringify({
          FROM: connectionData.FROM,
          EFI: connectionData.EFI,
          EFO: connectionData.EFO,
          TO: connectionData.TO,
        }),
        token,
        emptyResponse: true,
        org,
      });
      successAlert(CREATE_ORG_TRANSLATION_TRANSLATIONS.successMsg);
      navigate("/configurationManagement/OrganizationTranslation");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      try {
        errorAlert(e);
      } catch (e) {
        errorAlert(CREATE_ORG_TRANSLATION_TRANSLATIONS.errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {EDIT_ORGANISATION_TRANSLATION_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IOrganizationTranslationData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  min?: number;
                  max?: number;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "TOGGLE":
                          return (
                            <ToggleField
                              labelText={field.label}
                              onChange={(val) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                })
                              }
                              value={connectionData[field.name] as boolean}
                              tooltipText={field.tooltipText}
                              defaultToggled
                            />
                          );
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val ? +val : undefined,
                                });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                connectionData[field.name] !== 0 &&
                                !connectionData[field.name]
                              }
                              value={connectionData[field.name] as number}
                              invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.lessThan} ${field.max}`}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        default:
                          throw new Error("Invalid Form");
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_ORG_TRANSLATION_TRANSLATIONS.cancelModal.header}
        modalDescriptionText={
          CREATE_ORG_TRANSLATION_TRANSLATIONS.cancelModal.description
        }
        onDelete={() =>
          navigate("/configurationManagement/OrganizationTranslation")
        }
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_ORG_TRANSLATION_TRANSLATIONS.btnLabels.cancelConfirmationLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_ORG_TRANSLATION_TRANSLATIONS.btnLabels.cancel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateOrgTranslation-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createTranslation}
            label={CREATE_ORG_TRANSLATION_TRANSLATIONS.btnLabels.create}
            dataCy="createOrgTranslation-"
          />
        </div>
      </div>
    </>
  );
};

export default NewTranslationForm;
